import { CognitoUserPool, CognitoUser, AuthenticationDetails, CognitoUserSession, CognitoUserAttribute } from 'amazon-cognito-identity-js';

export interface UserData {
  email: string;
  email_verified: boolean;
  sub: string;
  given_name?: string;
  family_name?: string;
  phone_number?: string;
  'custom:company'?: string;
  'custom:organization'?: string;
  'custom:role'?: string;
  'custom:title'?: string;
  'custom:phone'?: string;
  'custom:account_owner'?: string;
  [key: string]: any;
}

// Initialize with environment variables (Amplify sets REACT_APP_COGNITO_*)
const userPool = process.env.REACT_APP_COGNITO_USER_POOL_ID && process.env.REACT_APP_COGNITO_CLIENT_ID
  ? new CognitoUserPool({
      UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
      ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID,
    })
  : null;

class AuthService {
  private currentUser: CognitoUser | null = null;
  private userPool: CognitoUserPool | null = userPool;

  async initializeForTenant(email: string): Promise<void> {
    // Use environment variables (Amplify sets REACT_APP_COGNITO_*)
    const poolData = {
      UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || '',
      ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID || '',
    };

    if (poolData.UserPoolId && poolData.ClientId) {
      try {
        this.userPool = new CognitoUserPool(poolData);
        return;
      } catch (error) {
        console.error('Failed to create CognitoUserPool with env vars:', error);
      }
    }

    throw new Error('Unable to initialize Cognito: No valid configuration found. Set REACT_APP_COGNITO_USER_POOL_ID and REACT_APP_COGNITO_CLIENT_ID.');
  }

  signUp(email: string, password: string, attributes: { [key: string]: string }) {
    return new Promise(async (resolve, reject) => {
      try {
        // Initialize tenant-specific UserPool if not already initialized
        if (!this.userPool) {
          await this.initializeForTenant(email);
        }

        if (!this.userPool) {
          reject(new Error('Cognito UserPool not initialized. Please check your configuration.'));
          return;
        }

        const attributeList: CognitoUserAttribute[] = [];

        Object.keys(attributes).forEach(key => {
          const attribute = new CognitoUserAttribute({
            Name: key,
            Value: attributes[key]
          });
          attributeList.push(attribute);
        });

        this.userPool!.signUp(email, password, attributeList, [], (err, result) => {
          if (err) {
            reject(err);
          } else {
            resolve(result);
          }
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  confirmSignUp(email: string, code: string) {
    return new Promise(async (resolve, reject) => {
      try {
        if (!this.userPool) {
          await this.initializeForTenant(email);
        }

        if (!this.userPool) {
          reject(new Error('Cognito UserPool not initialized. Please check your configuration.'));
          return;
        }

        const cognitoUser = new CognitoUser({
          Username: email,
          Pool: this.userPool!
        });

        cognitoUser.confirmRegistration(code, true, (err, result) => {
          if (err) {
            reject(err);
          } else {
            resolve(result);
          }
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  resendConfirmationCode(email: string) {
    return new Promise(async (resolve, reject) => {
      try {
        if (!this.userPool) {
          await this.initializeForTenant(email);
        }

        if (!this.userPool) {
          reject(new Error('Cognito UserPool not initialized. Please check your configuration.'));
          return;
        }

        const cognitoUser = new CognitoUser({
          Username: email,
          Pool: this.userPool!
        });

        cognitoUser.resendConfirmationCode((err, result) => {
          if (err) {
            reject(err);
          } else {
            resolve(result);
          }
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  signIn(email: string, password: string, contextData?: any) {
    return new Promise<CognitoUserSession>(async (resolve, reject) => {
      try {
        // Always initialize for tenant to ensure proper tenant-specific configuration
        await this.initializeForTenant(email);

        if (!this.userPool) {
          reject(new Error('Cognito UserPool not initialized. Please check your configuration.'));
          return;
        }

        const authenticationDetails = new AuthenticationDetails({
          Username: email,
          Password: password,
        });

        const cognitoUser = new CognitoUser({
          Username: email,
          Pool: this.userPool,
        });

        this.currentUser = cognitoUser;

        // Add context data for AWS Cognito Advanced Security
        if (contextData && process.env.REACT_APP_COGNITO_ADVANCED_SECURITY === 'true') {
          (cognitoUser as any).setAuthenticationFlowType('USER_SRP_AUTH');
          (authenticationDetails as any).setContextData(contextData);
        }

        cognitoUser.authenticateUser(authenticationDetails, {
          onSuccess: (result) => {
            resolve(result);
          },
          onFailure: (err) => {
            reject(err);
          },
          newPasswordRequired: (userAttributes) => {
            reject(new Error('New password required'));
          },
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  signOut() {
    if (!this.userPool) {
      sessionStorage.clear();
      localStorage.clear();
      return;
    }
    const user = this.userPool!.getCurrentUser();
    if (user) {
      user.signOut();
      this.currentUser = null;
    }
    sessionStorage.clear();
    localStorage.clear();
  }

  getCurrentUser() {
    if (!this.userPool) return null;
    return this.userPool!.getCurrentUser();
  }

  getSession(): Promise<CognitoUserSession> {
    return new Promise((resolve, reject) => {
      const user = this.getCurrentUser();
      if (!user) {
        reject(new Error('No user'));
        return;
      }

      user.getSession((err: any, session: CognitoUserSession) => {
        if (err) {
          reject(err);
        } else {
          resolve(session);
        }
      });
    });
  }

  getUserData(): Promise<UserData> {
    return new Promise((resolve, reject) => {
      const user = this.getCurrentUser();
      if (!user) {
        reject(new Error('No user'));
        return;
      }

      user.getSession((err: any, session: CognitoUserSession) => {
        if (err) {
          reject(err);
          return;
        }

        user.getUserAttributes((err: any, attributes: any) => {
          if (err) {
            reject(err);
            return;
          }

          const userData: UserData = {
            email: '',
            email_verified: false,
            sub: '',
          };

          attributes?.forEach((attribute: any) => {
            const name = attribute.getName();
            const value = attribute.getValue();
            if (name === 'email') userData.email = value;
            if (name === 'email_verified') userData.email_verified = value === 'true';
            if (name === 'sub') userData.sub = value;
            if (name === 'given_name') userData.given_name = value;
            if (name === 'family_name') userData.family_name = value;
            if (name === 'phone_number') userData.phone_number = value;
            if (name === 'custom:company') userData['custom:company'] = value;
            if (name === 'custom:organization') userData['custom:organization'] = value;
            if (name === 'custom:role') userData['custom:role'] = value;
            if (name === 'custom:title') userData['custom:title'] = value;
            if (name === 'custom:phone') userData['custom:phone'] = value;
            if (name === 'custom:account_owner') userData['custom:account_owner'] = value;
          });

          resolve(userData);
        });
      });
    });
  }

  async getIdToken(): Promise<string> {
    // Try Cognito SDK session first (set by signIn via SDK)
    try {
      const session = await this.getSession();
      return session.getIdToken().getJwtToken();
    } catch {
      // Fallback: check niwt_id_token in localStorage (set by registration auto-login)
      const storedToken = localStorage.getItem('niwt_id_token');
      if (storedToken) {
        // Verify token isn't expired
        try {
          const payload = JSON.parse(atob(storedToken.split('.')[1]));
          if (payload.exp && payload.exp * 1000 > Date.now()) {
            return storedToken;
          }
        } catch {
          // Token parse failed — fall through to error
        }
      }
      throw new Error('Unable to get ID token');
    }
  }

  forgotPassword(email: string) {
    return new Promise(async (resolve, reject) => {
      try {
        if (!this.userPool) {
          await this.initializeForTenant(email);
        }

        if (!this.userPool) {
          reject(new Error('Cognito UserPool not initialized. Please check your configuration.'));
          return;
        }

        const cognitoUser = new CognitoUser({
          Username: email,
          Pool: this.userPool,
        });

        cognitoUser.forgotPassword({
          onSuccess: (data) => {
            resolve(data);
          },
          onFailure: (err) => {
            reject(err);
          },
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  confirmPassword(email: string, code: string, newPassword: string) {
    return new Promise(async (resolve, reject) => {
      try {
        if (!this.userPool) {
          await this.initializeForTenant(email);
        }

        if (!this.userPool) {
          reject(new Error('Cognito UserPool not initialized. Please check your configuration.'));
          return;
        }

        const cognitoUser = new CognitoUser({
          Username: email,
          Pool: this.userPool,
        });

        cognitoUser.confirmPassword(code, newPassword, {
          onSuccess: (data) => {
            resolve(data);
          },
          onFailure: (err) => {
            reject(err);
          },
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  changePassword(oldPassword: string, newPassword: string) {
    return new Promise((resolve, reject) => {
      const user = this.getCurrentUser();
      if (!user) {
        reject(new Error('No user'));
        return;
      }

      user.getSession((err: any, session: CognitoUserSession) => {
        if (err) {
          reject(err);
          return;
        }

        user.changePassword(oldPassword, newPassword, (err: any, result: any) => {
          if (err) {
            reject(err);
          } else {
            resolve(result);
          }
        });
      });
    });
  }

  updateUserAttributes(attributes: { [key: string]: string }) {
    return new Promise((resolve, reject) => {
      const user = this.getCurrentUser();
      if (!user) {
        reject(new Error('No user'));
        return;
      }

      user.getSession((err: any, session: CognitoUserSession) => {
        if (err) {
          reject(err);
          return;
        }

        const attributeList: CognitoUserAttribute[] = [];

        Object.keys(attributes).forEach(key => {
          const attribute = new CognitoUserAttribute({
            Name: key,
            Value: attributes[key]
          });
          attributeList.push(attribute);
        });

        user.updateAttributes(attributeList, (err: any, result: any) => {
          if (err) {
            reject(err);
          } else {
            resolve(result);
          }
        });
      });
    });
  }

  // Method to check if user exists in session without initializing tenant
  hasExistingSession(): boolean {
    // Check if there's any session data that might indicate a user exists
    const hasSessionStorage = sessionStorage.getItem('CognitoIdentityServiceProvider.amplify-nextjs-web-app.LastAuthUser') !== null;
    const hasLocalStorage = Object.keys(localStorage).some(key => key.startsWith('CognitoIdentityServiceProvider'));

    return hasSessionStorage || hasLocalStorage;
  }

  // Method to initialize from existing session (for app startup)
  async initializeFromExistingSession(): Promise<boolean> {
    try {
      // Try to find existing session data to extract email
      const keys = Object.keys(localStorage).filter(key =>
        key.includes('CognitoIdentityServiceProvider') && key.endsWith('.idToken')
      );

      if (keys.length === 0) {
        return false;
      }

      // Extract email from JWT token to determine tenant
      const tokenKey = keys[0];
      const token = localStorage.getItem(tokenKey);
      if (!token) {
        return false;
      }

      // Decode JWT token to get email (without verification for tenant detection only)
      const base64Payload = token.split('.')[1];
      const payload = JSON.parse(atob(base64Payload));
      const email = payload.email;

      if (email) {
        await this.initializeForTenant(email);
        return true;
      }

      return false;
    } catch (error) {
      console.error('Failed to initialize from existing session:', error);
      return false;
    }
  }
}

export default new AuthService();
